import React, { useEffect, useState } from "react";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import toast from "react-hot-toast";
import moment from "moment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Card,
  CardBody,
  CardHeader,
  Chip,
  Option,
  Select,
  Typography,
} from "@material-tailwind/react";
import { Pie } from "react-chartjs-2";
import {
  CisoDashboardSkeletonRow1,
  CisoDashboardSkeletonRow2,
} from "../../components/Loaders/CisoDashboardSkeleton";
import SkeletonGrid from "../../components/Loaders/SkeletonGrid";
import {
  getCisoDashboardData,
  getPieChartData,
} from "../../helperFunctions/apiFunction";

const CisoDashboard = () => {
  const { refreshToken } = useAuthUserToken();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStandard, setIsLoadingStandard] = useState(false);
  const [templateData, setTemplateData] = useState([]);
  const [selectedOptionData, setSelectedOptionData] = useState("");
  const [chartData, setChartData] = useState([]);
  const [isoHealth, setIsoHealth] = useState("");
  const [pciHealth, setPciHealth] = useState("");
  const [isoControl, setIsoControl] = useState({ ok: 0, fail: 0 });
  const [pciControl, setPciControl] = useState({ ok: 0, fail: 0 });
  const [auditData, setAuditData] = useState([]);

  const TABLE_HEAD = ["NAME", "HEALTH", "CONTROL"];
  const TABLE_HEAD_AUDIT = ["NAME", "HEALTH", "STATUS", "DEADLINE"];
  const TABLE_ROWS = [
    {
      name: "PCI DSS v4",
      health: "At-Risk",
    },
    {
      name: "ISO27001:2022",
      health: "Healthy",
    },
  ];

  useEffect(() => {
    setIsLoading(null);
    getCisoDashboardData(refreshToken).then((data) => {
      if (data?.code === 440)
        toast.error(data?.message, {
          className: "text-[12px]",
        });
      setTemplateData(data?.data);
      setIsLoading(false);
    });
  }, []);

  const calculateHealthStatus = (audit) => {
    const now = moment();
    const endDate = moment(audit.auditEndDate);
    const daysBeforeDeadline = endDate.diff(now, "days");
    const isCompleted = audit.auditStatus.toLowerCase() === "completed";

    if (!isCompleted && daysBeforeDeadline <= 5) {
      return "At-Risk";
    } else if (isCompleted && now.isAfter(endDate)) {
      return "Healthy (Delayed)";
    } else if (isCompleted && now.isBefore(endDate)) {
      return "Healthy";
    } else if (!isCompleted && now.isAfter(endDate)) {
      return "At-Risk";
    } else {
      return "Healthy";
    }
  };

  useEffect(() => {
    if (templateData?.auditData) {
      const updatedAuditData = templateData.auditData?.results.map((audit) => ({
        ...audit,
        healthStatus: calculateHealthStatus(audit),
      }));
      setAuditData(updatedAuditData);
    }
  }, [templateData]);

  useEffect(() => {
    const healthCount = (items, scanType) => {
      let okCount = 0;
      let failCount = 0;
      let healthStatus = "Healthy"; // Default value

      items?.forEach((item) => {
        okCount += item?.testStatusCounts?.OK || 0;
        failCount += item?.testStatusCounts?.FAIL || 0;
        if (item?.compliantStatus === "non-compliant") {
          healthStatus = "At-Risk";
        }
      });

      // Update health status after the loop
      if (scanType === "iso") {
        setIsoHealth(healthStatus);
      } else {
        setPciHealth(healthStatus);
      }

      return { okCount, failCount };
    };

    const isoItems = templateData?.scanData?.filter(
      (item) => item?.scanType === "iso"
    );
    const pciItems = templateData?.scanData?.filter(
      (item) => item?.scanType === "pci"
    );

    const { okCount: isoOkCount, failCount: isoFailCount } = healthCount(
      isoItems,
      "iso"
    );
    const { okCount: pciOkCount, failCount: pciFailCount } = healthCount(
      pciItems,
      "pci"
    );

    setIsoControl({
      ok: isoOkCount,
      fail: isoFailCount,
    });
    setPciControl({
      ok: pciOkCount,
      fail: pciFailCount,
    });
  }, [templateData]);

  useEffect(() => {
    setIsLoadingStandard(true);
    getPieChartData(refreshToken, selectedOptionData)
      .then((data) => {
        if (Array.isArray(data)) {
          setChartData(data);
          setIsLoadingStandard(false);
        } else {
          console.error("Fetched data is not an array:", data);
          setIsLoadingStandard(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsLoadingStandard(false);
      });
  }, [selectedOptionData]);

  return (
    <div className="app-main__inner !pl-20">
      <Card
        className="p-12 mb-6 pt-6 rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible !m-0"
        >
          <div className="text-[24px] pb-4 font-black text-black">
            Welcome to{" "}
            <span className="text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text relative">
              TrustSpike Compliance Management Platform!
            </span>
          </div>
        </CardHeader>

        <CardBody className="!p-0">
          {/* Program table -1 */}
          <div className="s1-p1 backdrop-blur-lg drop-shadow-md">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 py-4 px-3 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Programs
                </h2>
                <MoreVertIcon />
              </div>
              <div className="overflow-x-auto w-full">
                <table className="w-full min-w-max text-left table-auto overflow-scroll">
                  <thead>
                    <tr>
                      {TABLE_HEAD.map((head) => (
                        <th
                          key={head}
                          className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50 w-32"
                        >
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                          >
                            {head}
                          </Typography>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  {isLoading === null ? (
                    <>
                      <tbody>
                        <tr>
                          <td colSpan={TABLE_HEAD.length}>
                            <CisoDashboardSkeletonRow1 />
                          </td>
                        </tr>
                      </tbody>
                    </>
                  ) : (
                    <>
                      <tbody>
                        {TABLE_ROWS.map(({ name, health }, index) => {
                          const isLast = index === TABLE_ROWS.length - 1;
                          const classes = isLast
                            ? "p-3"
                            : "p-3 border-b border-blue-gray-50";
                          return (
                            <tr key={`${name}-${index}`}>
                              <td className={classes}>
                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="font-normal"
                                >
                                  {name}
                                </Typography>
                              </td>
                              <td className={classes}>
                                <div className="w-max text-center">
                                  <Chip
                                    variant="ghost"
                                    size="sm"
                                    color={
                                      health !== "Healthy" ? "orange" : "green"
                                    }
                                    value={
                                      name === "PCI DSS v4"
                                        ? pciHealth
                                        : isoHealth
                                    }
                                  />
                                </div>
                              </td>
                              <td className={classes}>
                                <div className="w-max flex gap-6">
                                  <Label
                                    value={
                                      name === "PCI DSS v4"
                                        ? pciControl.ok
                                        : isoControl.ok
                                    }
                                    data={"OK"}
                                    color="green"
                                  />
                                  <Label
                                    value={
                                      name === "PCI DSS v4"
                                        ? pciControl.fail
                                        : isoControl.fail
                                    }
                                    data={"FAIL"}
                                    color="red"
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </>
                  )}
                </table>
              </div>
            </div>
          </div>

          {/* Audit table 2 */}
          <div className="my-10 backdrop-blur-lg drop-shadow-md">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 py-4 px-3 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">Audits</h2>
                <MoreVertIcon />
              </div>
              <div className="overflow-x-auto w-full">
                <table className="w-full min-w-max table-auto text-left">
                  <thead>
                    <tr>
                      {TABLE_HEAD_AUDIT.map((head) => (
                        <th
                          key={head}
                          className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50"
                        >
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                          >
                            {head}
                          </Typography>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  {isLoading === null ? (
                    <>
                      <tbody>
                        <tr>
                          <td colSpan={TABLE_HEAD_AUDIT.length}>
                            <CisoDashboardSkeletonRow2 />
                          </td>
                        </tr>
                      </tbody>
                    </>
                  ) : (
                    <>
                      <tbody>
                        {isLoading === false &&
                          auditData &&
                          Array.isArray(templateData?.auditData?.results) &&
                          auditData.length > 0 ? (
                          auditData.map(
                            (
                              {
                                auditName,
                                auditStatus,
                                auditEndDate,
                                healthStatus,
                                _id,
                              },
                              index
                            ) => {
                              const isLast =
                                index ===
                                templateData.auditData.results.length - 1;
                              const classes = isLast
                                ? "p-3"
                                : "p-3 border-b border-blue-gray-50";

                              return (
                                <tr key={`${_id}-${index}`}>
                                  <td className={classes}>
                                    <Typography
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                    >
                                      {auditName}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <div className="w-max">
                                      <Chip
                                        variant="ghost"
                                        size="sm"
                                        value={
                                          healthStatus === "At-Risk"
                                            ? "At-Risk"
                                            : healthStatus ===
                                              "Healthy (Delayed)"
                                              ? "Healthy (Delayed)"
                                              : healthStatus === "Healthy"
                                                ? "Healthy"
                                                : "Loading..."
                                        }
                                        color={
                                          healthStatus === "At-Risk"
                                            ? "orange"
                                            : healthStatus ===
                                              "Healthy (Delayed)"
                                              ? "yellow"
                                              : healthStatus === "Healthy"
                                                ? "green"
                                                : "blue-gray"
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td className={classes}>
                                    <div className="w-max">
                                      <Chip
                                        variant="ghost"
                                        size="sm"
                                        value={
                                          auditStatus === "schedule"
                                            ? "Schedule"
                                            : auditStatus === "inProgress"
                                              ? "In Progress"
                                              : auditStatus === "completed"
                                                ? "Completed"
                                                : auditStatus === "onHold"
                                                  ? "On Hold"
                                                  : auditStatus === "cancelled"
                                                    ? "Cancelled"
                                                    : "None"
                                        }
                                        color={
                                          auditStatus === "schedule"
                                            ? "blue"
                                            : auditStatus === "inProgress"
                                              ? "yellow"
                                              : auditStatus === "completed"
                                                ? "green"
                                                : auditStatus === "onHold"
                                                  ? "orange"
                                                  : auditStatus === "cancelled"
                                                    ? "red"
                                                    : "blue-gray"
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td className={classes}>
                                    <Chip
                                      variant="ghost"
                                      size="sm"
                                      value={
                                        auditEndDate
                                          ? moment(auditEndDate).format(
                                            "MMMM DD, YYYY"
                                          )
                                          : ""
                                      }
                                      className="rounded-full w-fit text-xs	normal-case font-medium	"
                                    />
                                  </td>
                                </tr>
                              );
                            }
                          )
                        ) : (
                          <tr>
                            <td colSpan="8">
                              <div className="flex flex-col items-center w-full">
                                <img
                                  quality={100}
                                  src="/assets/images/9276430.jpg"
                                  className="w-[350px] mix-blend-darken"
                                  alt="no-data-found"
                                />
                                <p className="text-[20px] font-medium">
                                  No Data Found!
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </>
                  )}
                </table>
              </div>
            </div>
          </div>

          {/* standard */}
          <div className="backdrop-blur-lg drop-shadow-md">
            <div className="border border-blue-gray-500 rounded-3xl bg-white w-full">
              <div className="border-b border-blue-gray-100/50 py-4 px-3 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Controls
                </h2>
                <MoreVertIcon />
              </div>
              <div className="!w-1/6 sm:w-1/4 md:w-1/3 lg:w-1/2 z-100 py-4 px-3">
                <Select
                  color="green"
                  label="Select Standard"
                  onChange={(event) => {
                    setSelectedOptionData(event);
                  }}
                  value={selectedOptionData}
                >
                  <Option value="">Select Standard</Option>
                  <Option value="pcidssv4data">PCI DSS 4</Option>
                  <Option value="pcidssv321data">PCI DSS 3.2.1</Option>
                </Select>
              </div>
            </div>
          </div>

          {selectedOptionData !== "" ? (
            isLoadingStandard ? (
              <>
                <SkeletonGrid />
              </>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 my-10">
                {chartData
                  .slice()
                  .sort((a, b) => a._id - b._id)
                  .sort((a, b) => {
                    const idA = isNaN(a._id) ? a._id : parseInt(a._id, 10);
                    const idB = isNaN(b._id) ? b._id : parseInt(b._id, 10);
                    return idA - idB;
                  })
                  .map((item, index) => {
                    const pieData = {
                      labels: ["Compliant", "Non-Compliant"],
                      datasets: [
                        {
                          data: [item.Compliant, item.NonCompliant],
                          backgroundColor: ["#66FF00", "rgb(212,0,0)"],
                        },
                      ],
                    };

                    return (
                      <div
                        key={item._id}
                        className="border border-blue-gray-500 rounded-3xl bg-white"
                      >
                        <div className="border-b border-blue-gray-100/50 px-3 py-4 flex justify-between items-center">
                          <h2 className="truncate w-auto font-bold text-base">
                            Requirement ID {item._id}
                          </h2>
                        </div>
                        <div className="my-3 px-3">
                          <Pie
                            data={pieData}
                            options={{
                              legend: {
                                display: false,
                              },
                              tooltips: {
                                enabled: false,
                              },
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )
          ) : (
            <></>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default CisoDashboard;

const Label = ({ value, data, color }) => {
  return (
    <div className="relative inline-flex">
      <div
        className="align-middle select-none font-sans font-bold text-center uppercase transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-xs py-2 px-4 rounded-lg bg-gray-900 text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none"
        type="button"
      >
        {value}
      </div>
      <span
        className={`absolute min-w-[12px] min-h-[12px] rounded-full py-1 px-1 text-xs font-medium leading-none grid place-items-center top-[4%] right-[2%] translate-x-2/4 -translate-y-2/4 bg-${color}-500 text-white`}
      >
        {data}
      </span>
    </div>
  );
};
